import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  Spinner,
  SpinnerSize,
  IconButton,
  DetailsList,
  IColumn,
  SelectionMode,
  CommandBar,
  ICommandBarItemProps,
  Dialog,
  DialogType,
  DialogFooter,
  SearchBox,
  Dropdown,
  IDropdownOption,
  Label,
  TextField,
  Toggle,
  Separator,
  ProgressIndicator,
  Panel,
  PanelType
} from '@fluentui/react';
import {
  ConnectorRegular,
  DataUsageRegular,
  ServerRegular,
  SearchRegular,
  AddRegular,
  PlayRegular,
  StopRegular,
  DeleteRegular,
  SettingsRegular,
  InfoRegular,
  DocumentRegular,
  FolderRegular,
  MailRegular,
  CloudRegular,
  DatabaseRegular,
  EyeRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  ErrorCircleRegular,
  ClockRegular,
  PlayCircleRegular,
  BugRegular,
  ShareRegular,
  GlobeRegular,
  BuildingRegular,
  StorageRegular,
  Database20Regular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import ConnectorHubDashboard from './ConnectorHubDashboard';
import JobManagement from './JobManagement';
import SharePointConnectorSetup from './SharePointConnectorSetup';
import PublicWebConnectorSetup from './PublicWebConnectorSetup';
import SharePointOnPremConnectorSetup from './SharePointOnPremConnectorSetup';
import { FileServerConnectorSetup } from './FileServerConnectorSetup';
import styles from './AIConnectorHub.module.css';

// Types
interface SearchIndex {
  id: string;
  name: string;
  documentCount: number;
  sizeGB: number;
  lastUpdated: string;
  status: 'active' | 'inactive' | 'indexing' | 'error';
  queryCount24h: number;
  avgResponseTime: number;
  connectorType: string;
}

interface IndexSchema {
  type: 'sharepoint_online' | 'sharepoint_server' | 'file_server' | 'onedrive' | 'exchange_online';
  fields: IndexField[];
  vectorEnabled: boolean;
  embeddingEnabled: boolean;
}

interface IndexField {
  name: string;
  type: 'string' | 'collection' | 'datetime' | 'boolean';
  searchable: boolean;
  filterable: boolean;
  sortable: boolean;
  facetable: boolean;
  retrievable: boolean;
}

interface ConnectorJob {
  id: string;
  name: string;
  connectorType: string;
  connectorTemplate: string;
  targetIndex: string;
  createNewIndex: boolean;
  status: 'created' | 'running' | 'completed' | 'failed' | 'queued' | 'stopped';
  progress?: number;
  created: string;
  createdBy: string;
  description: string;
  documentsProcessed: number;
  lastRun?: string;
  nextRun?: string;
  runCount: number;
  avgRunTime: number;
  successRate: number;
  configuration: ConnectorConfig;
  schedule?: {
    enabled: boolean;
    frequency: string;
    time: string;
    timezone: string;
  };
  emailFilters?: any;
  logs?: JobLog[];
  // Legacy/computed properties for backward compatibility
  startTime?: string;
  endTime?: string;
  itemsProcessed?: number;
  itemsTotal?: number;
}

interface ConnectorConfig {
  site_url: string;
  libraries: string[];
  file_types: string[];
  crawl_subsites: boolean;
  enable_embeddings: boolean;
  enable_acl: boolean;
  // Legacy fields for backward compatibility
  sourceUrl?: string;
  crawlSubsites?: boolean;
  contentTypes?: string[];
  fileTypes?: string[];
  aclEnabled?: boolean;
  crawlMode?: 'full' | 'delta';
  azureAppRegistration?: {
    clientId: string;
    tenantId: string;
    clientSecret: string;
  };
}

interface JobLog {
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  message: string;
}

interface ConnectorTemplate {
  id: string;
  name: string;
  type: 'sharepoint_online' | 'sharepoint_server' | 'file_server' | 'public_website' | 'database' | 'api' | 'dynamics_365' | 'priority_software' | 'power_bi' | 'google_workspace' | 'onedrive' | 'exchange_online' | 'database_connector';
  description: string;
  icon: React.ReactNode;
  borderColor?: string;
  category: 'Microsoft' | 'File Systems' | 'Web' | 'Databases' | 'APIs' | 'Business Applications' | 'Cloud Services' | 'Google';
  complexity: 'Simple' | 'Moderate' | 'Advanced';
  estimatedSetupTime: string;
  features: string[];
  requirements: string[];
  version: string;
  lastUpdated: string;
  supported: boolean;
}

interface LogEntry {
  id: string;
  timestamp: string;
  jobId: string;
  jobName: string;
  jobType: string;
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  details?: string;
  duration?: number;
  itemsProcessed?: number;
  errors?: string[];
  metadata?: Record<string, any>;
}

const AIConnectorHub: React.FC = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  
  // Data state
  const [indexes, setIndexes] = useState<SearchIndex[]>([]);
  const [connectorJobs, setConnectorJobs] = useState<ConnectorJob[]>([]);
  
  // Monitoring state (moved from renderMonitoring)
  const [runningJobLogs, setRunningJobLogs] = useState<{[jobId: string]: LogEntry[]}>({});
  const [logFilter, setLogFilter] = useState({
    searchQuery: ''
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [selectedLogEntry, setSelectedLogEntry] = useState<LogEntry | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);

  const [connectorTemplates, setConnectorTemplates] = useState<ConnectorTemplate[]>([]);

  // UI state
  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
  const [showCreateIndexDialog, setShowCreateIndexDialog] = useState(false);
  const [showIndexDetailsDialog, setShowIndexDetailsDialog] = useState(false);
  const [showJobDetailsPanel, setShowJobDetailsPanel] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ConnectorJob | null>(null);
  const [showSharePointSetup, setShowSharePointSetup] = useState(false);
  const [showPublicWebSetup, setShowPublicWebSetup] = useState(false);
  const [showSharePointOnPremSetup, setShowSharePointOnPremSetup] = useState(false);
  const [showFileServerSetup, setShowFileServerSetup] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Monitoring useEffects (moved from renderMonitoring)
  useEffect(() => {
    if (selectedTab === 'monitoring') {
      loadRunningJobLogs();
      
      let interval: NodeJS.Timeout;
      if (autoRefresh) {
        interval = setInterval(loadRunningJobLogs, refreshInterval);
      }
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [selectedTab, autoRefresh, refreshInterval]);

  const loadData = async () => {
    try {
      console.log('AIConnectorHub: Starting to load data...');
      setLoading(true);
      // Load all data including connector templates
      await Promise.all([
        loadIndexes(),
        loadConnectorJobs(),
        loadConnectorTemplates()
      ]);
      console.log('AIConnectorHub: Data loaded successfully');
    } catch (error) {
      console.error('AIConnectorHub: Error loading data:', error);
      setMessage({
        text: 'Failed to load connector hub data',
        type: MessageBarType.error
      });
    } finally {
      console.log('AIConnectorHub: Setting loading to false');
      setLoading(false);
    }
  };

  const loadIndexes = async () => {
    try {
      console.log('AIConnectorHub: Loading indexes...');
      const response = await authenticatedFetch('/api/connector-hub/indexes');
      const result = await response.json();
      
      if (result.success) {
        console.log('AIConnectorHub: Indexes loaded successfully:', result.indexes.length);
        setIndexes(result.indexes);
      } else {
        throw new Error(result.error || 'Failed to load indexes');
      }
    } catch (error) {
      console.error('Error loading indexes:', error);
      // Use empty array instead of mock data
      console.log('AIConnectorHub: Failed to load indexes, using empty array');
      setIndexes([]);
    }
  };

  const loadConnectorJobs = async () => {
    try {
      console.log('AIConnectorHub: Loading connector jobs...');
      const response = await authenticatedFetch('/api/connector-hub/jobs');
      const result = await response.json();
      
      if (result.success) {
        console.log('AIConnectorHub: Connector jobs loaded successfully:', result.jobs.length);
        setConnectorJobs(result.jobs);
      } else {
        throw new Error(result.error || 'Failed to load connector jobs');
      }
    } catch (error) {
      console.error('Error loading connector jobs:', error);
      // Use empty array instead of mock data
      console.log('AIConnectorHub: Failed to load connector jobs, using empty array');
      setConnectorJobs([]);
    }
  };

  const loadConnectorTemplates = async () => {
    try {
      console.log('AIConnectorHub: Loading connector templates...');
      const response = await authenticatedFetch('/api/connector-hub/connector-types');
      const result = await response.json();
      
      if (result.success && Array.isArray(result.connector_types)) {
        console.log('AIConnectorHub: Connector templates loaded from API:', result.connector_types.length);
        
        // Map icons to connector types
        const templatesWithIcons = result.connector_types.map((template: any) => ({
          ...template,
          icon: getConnectorIcon(template.type),
          borderColor: getConnectorBorderColor(template.category)
        }));
        
        setConnectorTemplates(templatesWithIcons);
      } else {
        throw new Error(result.error || 'Failed to load connector templates');
      }
    } catch (error) {
      console.error('Error loading connector templates:', error);
      console.log('AIConnectorHub: Failed to load connector templates, using empty array');
      setConnectorTemplates([]);
    }
  };

  // Icon mapping for different connector types
  const getConnectorIcon = (type: string) => {
    const iconProps = { style: { fontSize: 24, color: 'inherit' } };
    
    switch (type) {
      case 'sharepoint_online':
        return <ShareRegular {...iconProps} style={{ ...iconProps.style, color: '#0078d4' }} />;
      case 'sharepoint_onprem':
      case 'sharepoint-onprem':
        return <ShareRegular {...iconProps} style={{ ...iconProps.style, color: '#0066cc' }} />;
      case 'file_server':
      case 'file-server':
        return <FolderRegular {...iconProps} style={{ ...iconProps.style, color: '#795548' }} />;
      case 'public_website':
      case 'public-web':
        return <GlobeRegular {...iconProps} style={{ ...iconProps.style, color: '#4caf50' }} />;
      case 'google_workspace':
        return <CloudRegular {...iconProps} style={{ ...iconProps.style, color: '#ea4335' }} />;
      case 'priority_software':
        return <BuildingRegular {...iconProps} style={{ ...iconProps.style, color: '#ff9800' }} />;
      case 'onedrive':
        return <CloudRegular {...iconProps} style={{ ...iconProps.style, color: '#0078d4' }} />;
      case 'database_connector':
        return <DatabaseRegular {...iconProps} style={{ ...iconProps.style, color: '#2196f3' }} />;
      default:
        return <ConnectorRegular {...iconProps} style={{ ...iconProps.style, color: '#666' }} />;
    }
  };

  // Color mapping for categories
  const getConnectorBorderColor = (category: string) => {
    switch (category) {
      case 'Microsoft':
        return '#0078d4';
      case 'Google':
        return '#ea4335';
      case 'File Systems':
        return '#795548';
      case 'Web':
        return '#4caf50';
      case 'Business Applications':
        return '#ff9800';
      case 'Databases':
        return '#2196f3';
      case 'Cloud Services':
        return '#9c27b0';
      default:
        return '#666';
    }
  };

  // Monitoring functions (moved from renderMonitoring)
  // Load running job logs from job progress data
  const loadRunningJobLogs = async () => {
    try {
      // Get running jobs
      const runningJobs = connectorJobs.filter(job => 
        job.status === 'running'
      );
      
      const newLogs: {[jobId: string]: LogEntry[]} = {};
      
      for (const job of runningJobs) {
        try {
          // Get logs from the logs endpoint
          const logsResponse = await authenticatedFetch(`/api/connector-hub/jobs/${job.id}/logs`);
          const logsData = await logsResponse.json();
          
          // Convert logs to LogEntry format
          if (logsData.success && logsData.logs && Array.isArray(logsData.logs)) {
            newLogs[job.id] = logsData.logs.map((log: any) => ({
              id: `${job.id}-${log.timestamp || Date.now()}-${Math.random()}`,
              timestamp: log.timestamp || new Date().toISOString(),
              level: (log.level || 'info').toLowerCase() as 'info' | 'warning' | 'error' | 'debug',
              message: log.message || '',
              jobId: job.id,
              jobType: job.connectorType || 'sharepoint',
              jobName: job.name || `Job ${job.id}`,
              details: log.details || ''
            }));
          } else {
            // Fallback to progress endpoint for any embedded logs
            const progressResponse = await authenticatedFetch(`/api/connector-hub/jobs/${job.id}/progress`);
            const progressData = await progressResponse.json();
            
            if (progressData.logs && Array.isArray(progressData.logs)) {
              newLogs[job.id] = progressData.logs.map((log: any) => ({
                id: `${job.id}-${Date.now()}-${Math.random()}`,
                timestamp: log.timestamp || new Date().toISOString(),
                level: (log.level || 'info').toLowerCase() as 'info' | 'warning' | 'error' | 'debug',
                message: log.message || log.text || '',
                jobId: job.id,
                jobType: job.connectorType || 'sharepoint',
                jobName: job.name || `Job ${job.id}`,
                details: log.details || ''
              }));
            }
          }
        } catch (error) {
          console.error(`Error loading logs for job ${job.id}:`, error);
        }
      }
      
      setRunningJobLogs(newLogs);
    } catch (error) {
      console.error('Error loading running job logs:', error);
    }
  };

  const filterLogs = () => {
    // Combine all logs from running jobs
    const allLogs = Object.values(runningJobLogs).flat();
    let filtered = allLogs;

    if (logFilter.searchQuery) {
      const query = logFilter.searchQuery.toLowerCase();
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(query) ||
        log.jobName.toLowerCase().includes(query) ||
        log.details?.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  // Get filtered logs for use in UI
  const filteredLogs = filterLogs();

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return '#d83b01';
      case 'warning': return '#ff8c00';
      case 'info': return '#0078d4';
      case 'debug': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error': return <ErrorCircleRegular style={{ color: '#d83b01' }} />;
      case 'warning': return <WarningRegular style={{ color: '#ff8c00' }} />;
      case 'info': return <InfoRegular style={{ color: '#0078d4' }} />;
      case 'debug': return <BugRegular style={{ color: '#6b7280' }} />;
      default: return <InfoRegular style={{ color: '#6b7280' }} />;
    }
  };

  const renderOverview = () => (
    <div className={styles.overviewContainer}>
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
              <DatabaseRegular style={{ fontSize: 32, color: '#0078d4' }} />
              <Stack>
                <Text variant="large" style={{ fontWeight: 600 }}>
                  {indexes.length}
                </Text>
                <Text variant="medium">Search Indexes</Text>
              </Stack>
            </Stack>
          </div>

          <div className={styles.statCard}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
              <ConnectorRegular style={{ fontSize: 32, color: '#107c10' }} />
              <Stack>
                <Text variant="large" style={{ fontWeight: 600 }}>
                  {connectorJobs.filter(j => j.status === 'running').length}
                </Text>
                <Text variant="medium">Active Jobs</Text>
              </Stack>
            </Stack>
          </div>

          <div className={styles.statCard}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
              <DocumentRegular style={{ fontSize: 32, color: '#d83b01' }} />
              <Stack>
                <Text variant="large" style={{ fontWeight: 600 }}>
                  {indexes.reduce((sum, idx) => sum + idx.documentCount, 0).toLocaleString()}
                </Text>
                <Text variant="medium">Total Documents</Text>
              </Stack>
            </Stack>
          </div>

          <div className={styles.statCard}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
              <CheckmarkCircleRegular style={{ fontSize: 32, color: '#107c10' }} />
              <Stack>
                <Text variant="large" style={{ fontWeight: 600 }}>
                  {connectorTemplates.filter(t => t.supported).length}
                </Text>
                <Text variant="medium">Available Connectors</Text>
              </Stack>
            </Stack>
          </div>
        </div>

        {/* Recent Activity */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="large" style={{ fontWeight: 600 }}>Recent Activity</Text>
          <div className={styles.activityList}>
            {connectorJobs.slice(0, 5).map(job => (
              <div key={job.id} className={styles.activityItem}>
                <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                  <div className={`${styles.statusIcon} ${styles[job.status]}`}>
                    {job.status === 'running' && <ClockRegular />}
                    {job.status === 'completed' && <CheckmarkCircleRegular />}
                    {job.status === 'failed' && <ErrorCircleRegular />}
                  </div>
                  <Stack tokens={{ childrenGap: 4 }} style={{ flex: 1 }}>
                    <Text variant="medium" style={{ fontWeight: 500 }}>{job.name}</Text>
                    <Text variant="small" style={{ color: '#666' }}>
                      {job.status === 'running' ? `${job.progress}% complete` : job.status}
                    </Text>
                  </Stack>
                  <Text variant="small" style={{ color: '#666' }}>
                    {job.startTime ? new Date(job.startTime).toLocaleDateString() : new Date(job.created).toLocaleDateString()}
                  </Text>
                </Stack>
              </div>
            ))}
          </div>
        </Stack>
      </Stack>
    </div>
  );

  const renderConnectors = () => {
    const categories = [...new Set(connectorTemplates.map(t => t.category))];
    const supportedCount = connectorTemplates.filter(t => t.supported).length;
    
    return (
      <div className={styles.connectorsContainer}>
        <Stack tokens={{ childrenGap: 24 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="xLarge" style={{ fontWeight: 600 }}>Available Connectors</Text>
              <Text variant="medium" style={{ color: '#666' }}>
                {supportedCount} of {connectorTemplates.length} connectors available • Choose a connector to integrate your data sources
              </Text>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
              <Text variant="small" style={{ color: '#666' }}>Filter by category:</Text>
              <Dropdown
                placeholder="All Categories"
                options={[
                  { key: 'all', text: 'All Categories' },
                  ...categories.map(cat => ({ key: cat, text: cat }))
                ]}
                styles={{ dropdown: { width: 150 } }}
              />
            </Stack>
          </Stack>

          {/* Modern Connectors Grid - Simplified */}
          <Stack tokens={{ childrenGap: 24 }}>
            <Text variant="large" style={{ fontWeight: 600, color: '#1a365d' }}>Available Connectors</Text>
            {categories.map(category => {
              const categoryConnectors = connectorTemplates.filter(t => t.category === category);
              if (categoryConnectors.length === 0) return null;
              
              return (
                <Stack key={category} tokens={{ childrenGap: 16 }}>
                  <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="mediumPlus" style={{ fontWeight: 600, color: '#667eea' }}>
                      {category}
                    </Text>
                    <Text variant="small" style={{ color: '#666' }}>
                      {categoryConnectors.length} connector{categoryConnectors.length !== 1 ? 's' : ''}
                    </Text>
                  </Stack>
                  <div className={styles.modernConnectorsGrid}>
                    {categoryConnectors.map(connector => renderConnectorCard(connector))}
                  </div>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </div>
    );
  };

  const renderConnectorCard = (connector: ConnectorTemplate) => (
    <div 
      key={connector.id} 
      className={`${styles.modernConnectorTile} ${!connector.supported ? styles.comingSoon : ''}`}
      style={{ 
        '--border-color': connector.borderColor || '#e2e8f0',
        '--hover-shadow-color': `${connector.borderColor}33` || '#0078d433'
      } as React.CSSProperties}
      onClick={() => connector.supported && handleCreateJob(connector.type)}
    >
      {/* Header with Icon and Status */}
      <div className={styles.tileHeader}>
        <div className={styles.iconContainer}>
          {connector.icon}
        </div>
        <div className={styles.statusBadges}>
          {!connector.supported && (
            <div className={styles.comingSoonBadge}>
              Coming Soon
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={styles.tileContent}>
        <Text variant="mediumPlus" className={styles.tileName}>
          {connector.name}
        </Text>
        
        <Text variant="small" className={styles.tileDescription}>
          {connector.description}
        </Text>

        {/* Metadata */}
        <div className={styles.tileMetadata}>
          <div className={styles.metadataItem}>
            <ClockRegular style={{ fontSize: 14 }} />
            <Text variant="small">{connector.estimatedSetupTime}</Text>
          </div>
          <div className={styles.metadataItem}>
            <SettingsRegular style={{ fontSize: 14 }} />
            <Text variant="small">{connector.complexity}</Text>
          </div>
        </div>

        {/* Features Preview */}
        <div className={styles.featuresPreview}>
          {(connector.features || []).slice(0, 3).map(feature => (
            <div key={feature} className={styles.featureTag}>
              <CheckmarkCircleRegular style={{ fontSize: 12 }} />
              <Text variant="small">{feature}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* Footer - Simplified */}
      <div className={styles.tileFooter}>
        <div className={styles.actionButton}>
          {connector.supported ? (
            <PrimaryButton
              text="Create New Job"
              styles={{
                root: {
                  backgroundColor: connector.borderColor,
                  borderColor: connector.borderColor,
                  minWidth: '120px',
                  height: '36px',
                  fontSize: '14px',
                  fontWeight: '600'
                }
              }}
            />
          ) : (
            <DefaultButton
              text="Coming Soon"
              disabled
              styles={{
                root: {
                  minWidth: '120px',
                  height: '36px',
                  fontSize: '14px'
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Hover Effect Border */}
      <div className={styles.tileBorder}></div>
    </div>
  );

  const handleCreateJob = (connectorType: string) => {
    if (connectorType === 'sharepoint_online') {
      setShowSharePointSetup(true);
    } else if (connectorType === 'sharepoint_onprem') {
      setShowSharePointOnPremSetup(true);
    } else if (connectorType === 'public_website') {
      setShowPublicWebSetup(true);
    } else if (connectorType === 'file_server') {
      setShowFileServerSetup(true);
    }
  };

  const handleViewIndexDetails = (index: SearchIndex) => {
    setSelectedIndex(index.id);
    setShowIndexDetailsDialog(true);
  };

  const handleClearIndex = async (index: SearchIndex) => {
    const confirmed = window.confirm(
      `Are you sure you want to clear all data from index "${index.name}"? This will remove all documents but keep the index structure.`
    );
    
    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/connector-hub/indexes/${index.id}/clear`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({
          text: `Successfully cleared ${result.documentsDeleted || 0} documents from index "${index.name}"`,
          type: MessageBarType.success
        });
        loadData(); // Reload to update document counts
      } else {
        setMessage({
          text: `Failed to clear index: ${result.error || 'Unknown error'}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error clearing index:', error);
      setMessage({
        text: `Error clearing index: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    }
  };

  const handleDeleteIndex = async (index: SearchIndex) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete index "${index.name}"? This will permanently remove the index and all its data. This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const response = await authenticatedFetch(`/api/connector-hub/indexes/${index.id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({
          text: `Successfully deleted index "${index.name}"`,
          type: MessageBarType.success
        });
        loadData(); // Reload to update the indexes list
      } else {
        setMessage({
          text: `Failed to delete index: ${result.error || 'Unknown error'}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error deleting index:', error);
      setMessage({
        text: `Error deleting index: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    }
  };

  const handleSharePointSetupComplete = async (config: any) => {
    try {
      // The SharePointConnectorSetup component has already created the job
      // Job is created but not started - user needs to start it manually
      
      // Close the setup panel
      setShowSharePointSetup(false);
      
      // Switch to jobs tab to show the newly created job
      setSelectedTab('jobs');
      
      // Reload data to show the new job
      await loadData();
      
    } catch (error) {
      console.error('Error handling SharePoint setup completion:', error);
      setMessage({
        text: `Error: ${error}`,
        type: MessageBarType.error
      });
    }
  };

  const handlePublicWebSetupComplete = async (config: any) => {
    try {
      // The PublicWebConnectorSetup component has already created the job
      console.log('Public Web setup completed:', config);
      
      // Close the setup panel
      setShowPublicWebSetup(false);
      
      // Switch to jobs tab to show the newly created job
      setSelectedTab('jobs');
      
      // Reload data to show the new job
      await loadData();
      
    } catch (error) {
      console.error('Error handling Public Web setup completion:', error);
      setMessage({
        text: `Error: ${error}`,
        type: MessageBarType.error
      });
    }
  };

  const handleSharePointOnPremSetupComplete = async (config: any) => {
    try {
      // The SharePointOnPremConnectorSetup component has already created the job
      console.log('SharePoint On-Premises setup completed:', config);
      
      // Close the setup panel
      setShowSharePointOnPremSetup(false);
      
      // Switch to jobs tab to show the newly created job
      setSelectedTab('jobs');
      
      // Reload data to show the new job
      await loadData();
      
    } catch (error) {
      console.error('Error handling SharePoint On-Premises setup completion:', error);
      setMessage({
        text: `Error: ${error}`,
        type: MessageBarType.error
      });
    }
  };

  const handleFileServerSetupComplete = async (config: any) => {
    try {
      // The FileServerConnectorSetup component has already created the job
      console.log('File Server setup completed:', config);
      
      // Close the setup panel
      setShowFileServerSetup(false);
      
      // Switch to jobs tab to show the newly created job
      setSelectedTab('jobs');
      
      // Reload data to show the new job
      await loadData();
      
    } catch (error) {
      console.error('Error handling File Server setup completion:', error);
      setMessage({
        text: `Error: ${error}`,
        type: MessageBarType.error
      });
    }
  };

  const handleStartJob = async (jobId: string) => {
    try {
      setMessage(null);
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/start`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Job started successfully - no popup needed
        await loadData(); // Reload to update status
      } else {
        setMessage({
          text: `Failed to start job: ${result.error || 'Unknown error'}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error starting job:', error);
      setMessage({
        text: `Error starting job: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    }
  };

  const handleStopJob = async (jobId: string) => {
    try {
      setMessage(null);
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}/cancel`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({
          text: 'Job stopped successfully!',
          type: MessageBarType.success
        });
        await loadData(); // Reload to update status
      } else {
        setMessage({
          text: `Failed to stop job: ${result.error || 'Unknown error'}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error stopping job:', error);
      setMessage({
        text: `Error stopping job: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    }
  };

  const handleDeleteJob = async (jobId: string, jobName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete job "${jobName}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      setMessage(null);
      const response = await authenticatedFetch(`/api/connector-hub/jobs/${jobId}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setMessage({
          text: `Job "${jobName}" deleted successfully!`,
          type: MessageBarType.success
        });
        await loadData(); // Reload to update the jobs list
      } else {
        setMessage({
          text: `Failed to delete job: ${result.error || 'Unknown error'}`,
          type: MessageBarType.error
        });
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      setMessage({
        text: `Error deleting job: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: MessageBarType.error
      });
    }
  };

  const renderIndexes = () => {
    const indexColumns: IColumn[] = [
      {
        key: 'name',
        name: 'Name',
        fieldName: 'name',
        minWidth: 200,
        maxWidth: 300,
        isResizable: true,
        onRender: (item: SearchIndex) => (
          <Stack>
            <Text variant="medium" style={{ fontWeight: 500 }}>{item.name}</Text>
            <Text variant="small" style={{ color: '#666' }}>{item.connectorType}</Text>
          </Stack>
        )
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: SearchIndex) => (
          <div className={`${styles.statusBadge} ${styles[item.status]}`}>
            {item.status}
          </div>
        )
      },
      {
        key: 'documentCount',
        name: 'Documents',
        fieldName: 'documentCount',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: SearchIndex) => (
          <Text>{item.documentCount.toLocaleString()}</Text>
        )
      },
      {
        key: 'schema',
        name: 'Type',
        fieldName: 'schema',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: SearchIndex) => (
          <Text>{item.connectorType.replace('_', ' ')}</Text>
        )
      },
      {
        key: 'lastUpdated',
        name: 'Last Updated',
        fieldName: 'lastUpdated',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: SearchIndex) => (
          <Text>{new Date(item.lastUpdated).toLocaleDateString()}</Text>
        )
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 150,
        onRender: (item: SearchIndex) => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <IconButton
              iconProps={{ iconName: 'View' }}
              title="View Details"
              onClick={() => handleViewIndexDetails(item)}
            />
            <IconButton
              iconProps={{ iconName: 'Clear' }}
              title="Clear Index Data"
              onClick={() => handleClearIndex(item)}
              styles={{ icon: { color: '#ff6900' } }}
            />
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Delete Index"
              onClick={() => handleDeleteIndex(item)}
              styles={{ icon: { color: '#d83b01' } }}
            />
          </Stack>
        )
      }
    ];

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="large" style={{ fontWeight: 600 }}>Search Indexes</Text>
          <PrimaryButton
            text="Create New Index"
            iconProps={{ iconName: 'Add' }}
            onClick={() => setShowCreateIndexDialog(true)}
          />
        </Stack>

        <DetailsList
          items={indexes}
          columns={indexColumns}
          selectionMode={SelectionMode.none}
          className={styles.detailsList}
        />
      </Stack>
    );
  };

  const renderJobs = () => {
    const jobColumns: IColumn[] = [
      {
        key: 'name',
        name: 'Job Name',
        fieldName: 'name',
        minWidth: 200,
        maxWidth: 300,
        isResizable: true,
        onRender: (item: ConnectorJob) => (
          <Stack>
            <Text variant="medium" style={{ fontWeight: 500 }}>{item.name}</Text>
            <Text variant="small" style={{ color: '#666' }}>{item.connectorType}</Text>
          </Stack>
        )
      },
      {
        key: 'indexName',
        name: 'Target Index',
        fieldName: 'indexName',
        minWidth: 150,
        maxWidth: 200
      },
      {
        key: 'status',
        name: 'Status',
        fieldName: 'status',
        minWidth: 100,
        maxWidth: 120,
        onRender: (item: ConnectorJob) => (
          <div className={`${styles.statusBadge} ${styles[item.status]}`}>
            {item.status}
          </div>
        )
      },
      {
        key: 'progress',
        name: 'Progress',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: ConnectorJob) => (
          <Stack tokens={{ childrenGap: 4 }}>
            <ProgressIndicator
              percentComplete={(item.progress || 0) / 100}
              barHeight={4}
            />
            <Text variant="small">{item.progress || 0}%</Text>
          </Stack>
        )
      },
      {
        key: 'items',
        name: 'Items',
        minWidth: 120,
        onRender: (item: ConnectorJob) => (
          <Text>{(item.itemsProcessed || 0).toLocaleString()} / {(item.itemsTotal || 0).toLocaleString()}</Text>
        )
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 150,
        onRender: (item: ConnectorJob) => (
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <IconButton
              iconProps={{ iconName: 'View' }}
              title="View Details"
              onClick={() => {
                setSelectedJob(item);
                setShowJobDetailsPanel(true);
              }}
            />
            {item.status === 'running' ? (
              <IconButton
                iconProps={{ iconName: 'Stop' }}
                title="Stop Job"
                onClick={() => handleStopJob(item.id)}
                styles={{ icon: { color: '#d83b01' } }}
              />
            ) : (
              <IconButton
                iconProps={{ iconName: 'Play' }}
                title="Start Job"
                onClick={() => handleStartJob(item.id)}
                styles={{ icon: { color: '#107c10' } }}
              />
            )}
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              title="Delete Job"
              onClick={() => handleDeleteJob(item.id, item.name)}
              styles={{ icon: { color: '#d83b01' } }}
            />
          </Stack>
        )
      }
    ];

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Text variant="large" style={{ fontWeight: 600 }}>Connector Jobs</Text>
          <PrimaryButton
            text="Create New Job"
            iconProps={{ iconName: 'Add' }}
            onClick={() => setSelectedTab('connectors')}
          />
        </Stack>

        <DetailsList
          items={connectorJobs}
          columns={jobColumns}
          selectionMode={SelectionMode.none}
          className={styles.detailsList}
        />
      </Stack>
    );
  };

  const renderMonitoring = () => {
    const logColumns: IColumn[] = [
      {
        key: 'timestamp',
        name: 'Time',
        minWidth: 120,
        maxWidth: 150,
        onRender: (item: LogEntry) => (
          <Text variant="small" style={{ fontFamily: 'monospace' }}>
            {new Date(item.timestamp).toLocaleTimeString()}
          </Text>
        )
      },
      {
        key: 'level',
        name: 'Level',
        minWidth: 80,
        maxWidth: 100,
        onRender: (item: LogEntry) => (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            {getLevelIcon(item.level)}
            <Text variant="small" style={{ color: getLevelColor(item.level), fontWeight: 500 }}>
              {item.level.toUpperCase()}
            </Text>
          </Stack>
        )
      },
      {
        key: 'job',
        name: 'Job',
        minWidth: 150,
        maxWidth: 200,
        onRender: (item: LogEntry) => (
          <Stack tokens={{ childrenGap: 2 }}>
            <Text variant="small" style={{ fontWeight: 500 }}>
              {item.jobName}
            </Text>
            <Text variant="small" style={{ color: '#666' }}>
              {item.jobType.replace('_', ' ')}
            </Text>
          </Stack>
        )
      },
      {
        key: 'message',
        name: 'Message',
        minWidth: 300,
        isResizable: true,
        onRender: (item: LogEntry) => (
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small">{item.message}</Text>
            {item.duration && (
              <Text variant="small" style={{ color: '#666' }}>
                Duration: {item.duration}ms
              </Text>
            )}
            {item.itemsProcessed && (
              <Text variant="small" style={{ color: '#666' }}>
                Items: {item.itemsProcessed.toLocaleString()}
              </Text>
            )}
          </Stack>
        )
      },
      {
        key: 'actions',
        name: 'Actions',
        minWidth: 50,
        maxWidth: 80,
        onRender: (item: LogEntry) => (
          <IconButton
            iconProps={{ iconName: 'View' }}
            title="View Details"
            onClick={() => {
              setSelectedLogEntry(item);
              setShowLogDetails(true);
            }}
          />
        )
      }
    ];

    return (
      <div className={styles.monitoringContainer}>
        <Stack tokens={{ childrenGap: 24 }}>
          {/* Real-time Statistics */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                <PlayCircleRegular style={{ fontSize: 32, color: '#107c10' }} />
                <Stack>
                  <Text variant="large" style={{ fontWeight: 600 }}>
                    {connectorJobs.filter(job => job.status === 'running').length}
                  </Text>
                  <Text variant="medium">Active Jobs</Text>
                </Stack>
              </Stack>
            </div>

            <div className={styles.statCard}>
              <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                <ErrorCircleRegular style={{ fontSize: 32, color: '#d83b01' }} />
                <Stack>
                  <Text variant="large" style={{ fontWeight: 600 }}>
                    {filteredLogs.filter(log => log.level === 'error').length}
                  </Text>
                  <Text variant="medium">Errors (24h)</Text>
                </Stack>
              </Stack>
            </div>

            <div className={styles.statCard}>
              <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                <WarningRegular style={{ fontSize: 32, color: '#ff8c00' }} />
                <Stack>
                  <Text variant="large" style={{ fontWeight: 600 }}>
                    {filteredLogs.filter(log => log.level === 'warning').length}
                  </Text>
                  <Text variant="medium">Warnings (24h)</Text>
                </Stack>
              </Stack>
            </div>

            <div className={styles.statCard}>
              <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center">
                <DocumentRegular style={{ fontSize: 32, color: '#0078d4' }} />
                <Stack>
                  <Text variant="large" style={{ fontWeight: 600 }}>
                    {filteredLogs.reduce((sum, log) => sum + (log.itemsProcessed || 0), 0).toLocaleString()}
                  </Text>
                  <Text variant="medium">Items Processed</Text>
                </Stack>
              </Stack>
            </div>
          </div>

          {/* Live Job Monitoring */}
          <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="large" style={{ fontWeight: 600 }}>Live Job Monitoring</Text>
              <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                <Toggle
                  label="Auto Refresh"
                  checked={autoRefresh}
                  onChange={(_, checked) => setAutoRefresh(!!checked)}
                />
                <Dropdown
                  options={[
                    { key: 1000, text: '1 second' },
                    { key: 5000, text: '5 seconds' },
                    { key: 10000, text: '10 seconds' },
                    { key: 30000, text: '30 seconds' }
                  ]}
                  selectedKey={refreshInterval}
                  onChange={(_, option) => setRefreshInterval(option?.key as number)}
                  styles={{ root: { width: 120 } }}
                />
              </Stack>
            </Stack>

            <div className={styles.jobMonitoringGrid}>
              {connectorJobs.filter(job => job.status === 'running').map(job => (
                <div key={job.id} className={styles.jobMonitorCard}>
                  <Stack tokens={{ childrenGap: 12 }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                      <Text variant="medium" style={{ fontWeight: 600 }}>{job.name}</Text>
                      <div className={`${styles.statusBadge} ${styles.running}`}>
                        <div className={styles.pulsingDot}></div>
                        LIVE
                      </div>
                    </Stack>
                    
                    <ProgressIndicator
                      label={`${(() => {
                        const progress = job.progress as any;
                        if (typeof progress === 'object' && progress?.percentage !== undefined) {
                          return progress.percentage;
                        }
                        return typeof progress === 'number' ? progress : 0;
                      })()}% complete`}
                      percentComplete={(() => {
                        const progress = job.progress as any;
                        if (typeof progress === 'object' && progress?.percentage !== undefined) {
                          return progress.percentage;
                        }
                        return typeof progress === 'number' ? progress : 0;
                      })() / 100}
                    />
                    
                    <Stack tokens={{ childrenGap: 4 }}>
                      <Text variant="small" style={{ color: '#666' }}>
                        <strong>Phase:</strong> {(() => {
                          const progress = job.progress as any;
                          if (typeof progress === 'object' && progress?.phase) {
                            return progress.phase;
                          }
                          return (job as any).currentPhase || 'Processing';
                        })()}
                      </Text>
                      <Text variant="small" style={{ color: '#666' }}>
                        <strong>Items:</strong> {(() => {
                          const progress = job.progress as any;
                          if (typeof progress === 'object' && progress?.items_processed !== undefined) {
                            const processed = progress.items_processed || 0;
                            const total = progress.total_items || 0;
                            return `${processed.toLocaleString()} / ${total.toLocaleString()}`;
                          }
                          return `${(job.itemsProcessed || 0).toLocaleString()} / ${(job.itemsTotal || 0).toLocaleString()}`;
                        })()}
                      </Text>
                      {(() => {
                        const progress = job.progress as any;
                        if (typeof progress === 'object' && progress?.current_item) {
                          return (
                            <Text variant="small" style={{ color: '#666', fontStyle: 'italic' }}>
                              {progress.current_item}
                            </Text>
                          );
                        }
                        return null;
                      })()}
                      {(job as any).metrics && (
                        <>
                          <Text variant="small" style={{ color: '#666' }}>
                            <strong>Documents:</strong> {((job as any).metrics.documents_processed || 0).toLocaleString()} / {((job as any).metrics.documents_discovered || 0).toLocaleString()}
                          </Text>
                          <Text variant="small" style={{ color: '#666' }}>
                            <strong>Chunks:</strong> {((job as any).metrics.chunks_indexed || 0).toLocaleString()}
                          </Text>
                          {(job as any).metrics.entities_extracted > 0 && (
                            <Text variant="small" style={{ color: '#666' }}>
                              <strong>Entities:</strong> {((job as any).metrics.entities_extracted || 0).toLocaleString()}
                            </Text>
                          )}
                          {(job as any).metrics.errors_count > 0 && (
                            <Text variant="small" style={{ color: '#d83b01' }}>
                              <strong>Errors:</strong> {((job as any).metrics.errors_count || 0).toLocaleString()}
                            </Text>
                          )}
                        </>
                      )}
                      <Text variant="small" style={{ color: '#666' }}>
                        ETA: {(() => {
                          const progress = job.progress as any;
                          let itemsTotal = 0;
                          let itemsProcessed = 0;
                          
                          if (typeof progress === 'object') {
                            itemsTotal = progress.total_items || job.itemsTotal || 0;
                            itemsProcessed = progress.items_processed || job.itemsProcessed || 0;
                          } else {
                            itemsTotal = job.itemsTotal || 0;
                            itemsProcessed = job.itemsProcessed || 0;
                          }
                          
                          return itemsTotal > itemsProcessed ? 
                            `${Math.ceil((itemsTotal - itemsProcessed) / Math.max(1, Math.floor(itemsProcessed / Math.max(1, Date.now() - new Date(job.created || Date.now()).getTime()) * 60000)))} minutes` : 
                            'Completing...';
                        })()}
                      </Text>
                      
                      {/* Live Logs Display */}
                      <Separator />
                      <Text variant="small" style={{ fontWeight: 600, color: '#333' }}>
                        Recent Logs
                      </Text>
                      <div style={{ 
                        maxHeight: '150px', 
                        overflowY: 'auto', 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        padding: '8px',
                        fontSize: '11px',
                        fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                      }}>
                        {runningJobLogs[job.id] && runningJobLogs[job.id].length > 0 ? (
                          runningJobLogs[job.id].slice(-5).map((logEntry, index) => {
                            // Color coding for log levels
                            let logColor = '#333';
                            switch (logEntry.level?.toUpperCase()) {
                              case 'ERROR':
                                logColor = '#d83b01';
                                break;
                              case 'WARNING':
                                logColor = '#ff8c00';
                                break;
                              case 'INFO':
                                logColor = '#0078d4';
                                break;
                              case 'DEBUG':
                                logColor = '#666';
                                break;
                            }
                            
                            const displayTime = logEntry.timestamp ? 
                              new Date(logEntry.timestamp).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                              }) : '';
                            
                            return (
                              <div key={index} style={{ 
                                marginBottom: '2px',
                                padding: '2px 4px',
                                borderLeft: `2px solid ${logColor}`,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)'
                              }}>
                                <span style={{ color: '#666', fontSize: '10px' }}>
                                  {displayTime}
                                </span>
                                {logEntry.level && (
                                  <span style={{ 
                                    color: logColor, 
                                    fontWeight: 'bold',
                                    marginLeft: '6px',
                                    fontSize: '10px'
                                  }}>
                                    [{logEntry.level}]
                                  </span>
                                )}
                                <span style={{ 
                                  color: '#333',
                                  marginLeft: logEntry.level ? '6px' : '6px',
                                  wordBreak: 'break-word'
                                }}>
                                  {logEntry.message || ''}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div style={{ 
                            color: '#666', 
                            textAlign: 'center', 
                            fontStyle: 'italic',
                            padding: '20px 0'
                          }}>
                            No recent logs available
                          </div>
                        )}
                      </div>
                    </Stack>
                  </Stack>
                </div>
              ))}
              {connectorJobs.filter(job => job.status === 'running').length === 0 && (
                <div className={styles.emptyJobsMessage}>
                  <PlayCircleRegular style={{ fontSize: 48, color: '#d1d5db' }} />
                  <Text style={{ color: '#666', textAlign: 'center' }}>
                    No jobs currently running
                  </Text>
                </div>
              )}
            </div>
          </Stack>

          {/* Log Filtering */}
          <Stack tokens={{ childrenGap: 16 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="large" style={{ fontWeight: 600 }}>Running Job Logs</Text>
              <IconButton
                iconProps={{ iconName: 'Refresh' }}
                title="Refresh Logs"
                onClick={loadRunningJobLogs}
              />
            </Stack>

            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="end" wrap>
              <SearchBox
                placeholder="Search running job logs..."
                value={logFilter.searchQuery}
                onChange={(_, value) => setLogFilter(prev => ({ ...prev, searchQuery: value || '' }))}
                styles={{ root: { width: 300 } }}
              />
            </Stack>
          </Stack>

          {/* Logs Table */}
          <div className={styles.logsTable}>
            <DetailsList
              items={filteredLogs}
              columns={logColumns}
              selectionMode={SelectionMode.none}
              compact={true}
              styles={{
                root: { maxHeight: '500px', overflowY: 'auto' }
              }}
            />
          </div>

          {/* Log Details Panel */}
          <Panel
            isOpen={showLogDetails}
            onDismiss={() => setShowLogDetails(false)}
            headerText="Log Entry Details"
            type={PanelType.medium}
          >
            {selectedLogEntry && (
              <Stack tokens={{ childrenGap: 20 }}>
                <Stack tokens={{ childrenGap: 12 }}>
                  <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                    {getLevelIcon(selectedLogEntry.level)}
                    <Text variant="large" style={{ fontWeight: 600 }}>
                      {selectedLogEntry.level.toUpperCase()}
                    </Text>
                    <Text variant="medium" style={{ color: '#666' }}>
                      {new Date(selectedLogEntry.timestamp).toLocaleString()}
                    </Text>
                  </Stack>

                  <Stack tokens={{ childrenGap: 8 }}>
                    <Label>Job Information</Label>
                    <Text><strong>Name:</strong> {selectedLogEntry.jobName}</Text>
                    <Text><strong>Type:</strong> {selectedLogEntry.jobType}</Text>
                    <Text><strong>ID:</strong> {selectedLogEntry.jobId}</Text>
                  </Stack>

                  <Stack tokens={{ childrenGap: 8 }}>
                    <Label>Message</Label>
                    <Text>{selectedLogEntry.message}</Text>
                  </Stack>

                  {selectedLogEntry.details && (
                    <Stack tokens={{ childrenGap: 8 }}>
                      <Label>Details</Label>
                      <div className={styles.logDetails}>
                        <Text style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px' }}>
                          {selectedLogEntry.details}
                        </Text>
                      </div>
                    </Stack>
                  )}

                  {selectedLogEntry.errors && selectedLogEntry.errors.length > 0 && (
                    <Stack tokens={{ childrenGap: 8 }}>
                      <Label>Errors</Label>
                      {selectedLogEntry.errors.map((error, index) => (
                        <Text key={index} style={{ color: '#d83b01' }}>• {error}</Text>
                      ))}
                    </Stack>
                  )}

                  {selectedLogEntry.metadata && (
                    <Stack tokens={{ childrenGap: 8 }}>
                      <Label>Metadata</Label>
                      <div className={styles.metadataContainer}>
                        <pre style={{ fontSize: '12px', margin: 0 }}>
                          {JSON.stringify(selectedLogEntry.metadata, null, 2)}
                        </pre>
                      </div>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}
          </Panel>
        </Stack>
      </div>
    );
  };

  const renderAdmin = () => (
    <div className={styles.adminContainer}>
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Admin Actions */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="large" style={{ fontWeight: 600 }}>System Administration</Text>
          
          <div className={styles.adminActionsGrid}>
            <div className={styles.adminActionCard}>
              <Stack tokens={{ childrenGap: 12 }}>
                <SettingsRegular style={{ fontSize: 32, color: '#0078d4' }} />
                <Text variant="medium" style={{ fontWeight: 600 }}>System Configuration</Text>
                <Text variant="small" style={{ color: '#666' }}>
                  Configure global settings and system parameters
                </Text>
                <PrimaryButton text="Configure" />
              </Stack>
            </div>

            <div className={styles.adminActionCard}>
              <Stack tokens={{ childrenGap: 12 }}>
                <DatabaseRegular style={{ fontSize: 32, color: '#107c10' }} />
                <Text variant="medium" style={{ fontWeight: 600 }}>Index Management</Text>
                <Text variant="small" style={{ color: '#666' }}>
                  Bulk operations on search indexes
                </Text>
                <DefaultButton text="Manage" />
              </Stack>
            </div>

            <div className={styles.adminActionCard}>
              <Stack tokens={{ childrenGap: 12 }}>
                <ServerRegular style={{ fontSize: 32, color: '#d83b01' }} />
                <Text variant="medium" style={{ fontWeight: 600 }}>Service Health</Text>
                <Text variant="small" style={{ color: '#666' }}>
                  Monitor and restart system services
                </Text>
                <DefaultButton text="Monitor" />
              </Stack>
            </div>

            <div className={styles.adminActionCard}>
              <Stack tokens={{ childrenGap: 12 }}>
                <DocumentRegular style={{ fontSize: 32, color: '#8b5cf6' }} />
                <Text variant="medium" style={{ fontWeight: 600 }}>Audit Logs</Text>
                <Text variant="small" style={{ color: '#666' }}>
                  View system and user activity logs
                </Text>
                <DefaultButton text="View Logs" />
              </Stack>
            </div>
          </div>
        </Stack>

        {/* System Information */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Text variant="large" style={{ fontWeight: 600 }}>System Information</Text>
          <div className={styles.systemInfoGrid}>
            <div className={styles.systemInfoCard}>
              <Text variant="medium" style={{ fontWeight: 600 }}>Version</Text>
              <Text variant="small">AI Connector Hub v2.1.0</Text>
            </div>
            <div className={styles.systemInfoCard}>
              <Text variant="medium" style={{ fontWeight: 600 }}>Uptime</Text>
              <Text variant="small">7 days, 14 hours</Text>
            </div>
            <div className={styles.systemInfoCard}>
              <Text variant="medium" style={{ fontWeight: 600 }}>Storage Used</Text>
              <Text variant="small">
                {indexes.reduce((sum, idx) => sum + idx.sizeGB, 0).toFixed(1)} GB
              </Text>
            </div>
          </div>
        </Stack>
      </Stack>
    </div>
  );

  // Create Index Dialog Component
  const CreateIndexDialog: React.FC<{
    onDismiss: () => void;
    onIndexCreated: (indexName: string) => void;
  }> = ({ onDismiss, onIndexCreated }) => {
    const [indexName, setIndexName] = useState('');
    const [connectorType, setConnectorType] = useState('sharepoint_online');
    const [vectorEnabled, setVectorEnabled] = useState(true);
    const [semanticEnabled, setSemanticEnabled] = useState(true);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCreate = async () => {
      if (!indexName.trim()) {
        setError('Index name is required');
        return;
      }

      setCreating(true);
      setError(null);

      try {
        const response = await authenticatedFetch('/api/connector-hub/indexes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: indexName.trim(),
            connector_type: connectorType,
            vector_enabled: vectorEnabled,
            semantic_enabled: semanticEnabled
          })
        });

        const result = await response.json();

        if (result.success) {
          onIndexCreated(indexName.trim());
        } else {
          setError(result.error || 'Failed to create index');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setCreating(false);
      }
    };

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        {error && (
          <MessageBar messageBarType={MessageBarType.error}>
            {error}
          </MessageBar>
        )}
        
        <TextField
          label="Index Name"
          value={indexName}
          onChange={(_, value) => setIndexName(value || '')}
          placeholder="Enter index name (e.g., my-sharepoint-index)"
          required
        />
        
        <Dropdown
          label="Connector Type"
          selectedKey={connectorType}
          options={[
            { key: 'sharepoint_online', text: 'SharePoint Online' },
            { key: 'sharepoint_onprem', text: 'SharePoint On-Premises' },
            { key: 'file_server', text: 'File Server' },
            { key: 'public_website', text: 'Public Website' }
          ]}
          onChange={(_, option) => setConnectorType(option?.key as string)}
        />
        
        <Toggle
          label="Enable Vector Search"
          checked={vectorEnabled}
          onChange={(_, checked) => setVectorEnabled(!!checked)}
        />
        
        <Toggle
          label="Enable Semantic Search"
          checked={semanticEnabled}
          onChange={(_, checked) => setSemanticEnabled(!!checked)}
        />

        <DialogFooter>
          <PrimaryButton
            text="Create Index"
            onClick={handleCreate}
            disabled={creating || !indexName.trim()}
          />
          <DefaultButton
            text="Cancel"
            onClick={onDismiss}
            disabled={creating}
          />
        </DialogFooter>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '400px' }}>
        <Spinner size={SpinnerSize.large} label="Loading AI Connector Hub..." />
      </Stack>
    );
  }

  return (
    <div className={styles.container}>
      <Stack tokens={{ childrenGap: 24 }}>
        {/* Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack tokens={{ childrenGap: 8 }}>
            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
              <ConnectorRegular className={styles.headerIcon} />
              <Text variant="xxLarge" className={styles.headerTitle}>
                AI Connector Hub
              </Text>
            </Stack>
            <Text variant="medium" className={styles.headerSubtitle}>
              Connect, index, and search your corporate data sources with Azure AI Search
            </Text>
          </Stack>
          <DefaultButton
            text="Refresh"
            iconProps={{ iconName: 'Refresh' }}
            onClick={loadData}
          />
        </Stack>

        {/* Messages */}
        {message && (
          <MessageBar
            messageBarType={message.type}
            onDismiss={() => setMessage(null)}
          >
            {message.text}
          </MessageBar>
        )}

        {/* Main Content */}
        <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey || 'dashboard')}>
          <PivotItem 
            headerText="Dashboard" 
            itemKey="dashboard"
            itemIcon="BarChartHorizontal"
          >
            <ConnectorHubDashboard />
          </PivotItem>
          
          <PivotItem 
            headerText="Job Management" 
            itemKey="jobs"
            itemIcon="PlaySolid"
          >
            <JobManagement onNavigateToConnectors={() => setSelectedTab('connectors')} />
          </PivotItem>
          
          <PivotItem 
            headerText="Connectors" 
            itemKey="connectors"
            itemIcon="PlugConnected"
          >
            {renderConnectors()}
          </PivotItem>
          
          <PivotItem 
            headerText="Indexes" 
            itemKey="indexes"
            itemIcon="Database"
          >
            {renderIndexes()}
          </PivotItem>

          <PivotItem 
            headerText="Monitoring" 
            itemKey="monitoring"
            itemIcon="ActivityFeed"
          >
            {renderMonitoring()}
          </PivotItem>

          <PivotItem 
            headerText="Admin" 
            itemKey="admin"
            itemIcon="Settings"
          >
            {renderAdmin()}
          </PivotItem>
          
          <PivotItem 
            headerText="Overview" 
            itemKey="overview"
            itemIcon="Info"
          >
            {renderOverview()}
          </PivotItem>
        </Pivot>

        {/* Job Details Panel */}
        <Panel
          isOpen={showJobDetailsPanel}
          onDismiss={() => setShowJobDetailsPanel(false)}
          headerText={selectedJob?.name || 'Job Details'}
          type={PanelType.medium}
        >
          {selectedJob && (
            <Stack tokens={{ childrenGap: 20 }}>
              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Job Status</Text>
                <div className={`${styles.statusBadge} ${styles[selectedJob.status]}`}>
                  {selectedJob.status}
                </div>
                <ProgressIndicator
                  label="Progress"
                  description={`${(selectedJob.itemsProcessed || 0).toLocaleString()} of ${(selectedJob.itemsTotal || 0).toLocaleString()} items processed`}
                  percentComplete={(selectedJob.progress || 0) / 100}
                />
              </Stack>

              <Separator />

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Configuration</Text>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Text variant="small"><strong>Connector Type:</strong> {selectedJob.connectorType}</Text>
                  <Text variant="small"><strong>Target Index:</strong> {selectedJob.targetIndex}</Text>
                  <Text variant="small"><strong>Site URL:</strong> {selectedJob.configuration?.site_url || 'N/A'}</Text>
                  <Text variant="small"><strong>File Types:</strong> {(selectedJob.configuration?.file_types || []).join(', ') || 'N/A'}</Text>
                  <Text variant="small"><strong>Libraries:</strong> {(selectedJob.configuration?.libraries || []).length > 0 ? selectedJob.configuration.libraries.join(', ') : 'All libraries'}</Text>
                  <Text variant="small"><strong>Crawl Subsites:</strong> {selectedJob.configuration?.crawl_subsites ? 'Yes' : 'No'}</Text>
                  <Text variant="small"><strong>ACL Enabled:</strong> {selectedJob.configuration?.enable_acl ? 'Yes' : 'No'}</Text>
                  <Text variant="small"><strong>Embeddings Enabled:</strong> {selectedJob.configuration?.enable_embeddings ? 'Yes' : 'No'}</Text>
                  <Text variant="small"><strong>Created:</strong> {selectedJob.created ? new Date(selectedJob.created).toLocaleString() : 'N/A'}</Text>
                  <Text variant="small"><strong>Schedule:</strong> {selectedJob.schedule?.enabled ? `${selectedJob.schedule.frequency} at ${selectedJob.schedule.time}` : 'Manual only'}</Text>
                </Stack>
              </Stack>

              <Separator />

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Actions</Text>
                <Stack horizontal tokens={{ childrenGap: 12 }}>
                  {selectedJob.status === 'running' ? (
                    <PrimaryButton
                      text="Stop Job"
                      iconProps={{ iconName: 'Stop' }}
                      onClick={() => {
                        handleStopJob(selectedJob.id);
                        setShowJobDetailsPanel(false);
                      }}
                      styles={{ root: { backgroundColor: '#d83b01' } }}
                    />
                  ) : (
                    <PrimaryButton
                      text="Start Job"
                      iconProps={{ iconName: 'Play' }}
                      onClick={() => {
                        handleStartJob(selectedJob.id);
                        setShowJobDetailsPanel(false);
                      }}
                    />
                  )}
                  <DefaultButton
                    text="Delete Job"
                    iconProps={{ iconName: 'Delete' }}
                    onClick={() => {
                      handleDeleteJob(selectedJob.id, selectedJob.name);
                      setShowJobDetailsPanel(false);
                    }}
                  />
                </Stack>
              </Stack>

              <Separator />

              <Stack tokens={{ childrenGap: 12 }}>
                <Text variant="medium" style={{ fontWeight: 600 }}>Recent Logs</Text>
                <div className={styles.logContainer}>
                  <Text variant="small" style={{ color: '#666' }}>
                    Logs will be displayed here once the job execution logging is fully implemented.
                  </Text>
                </div>
              </Stack>
            </Stack>
          )}
        </Panel>

        {/* SharePoint Connector Setup */}
        <SharePointConnectorSetup
          isVisible={showSharePointSetup}
          onDismiss={() => setShowSharePointSetup(false)}
          onComplete={handleSharePointSetupComplete}
        />

        {/* Public Web Connector Setup */}
        <PublicWebConnectorSetup
          isVisible={showPublicWebSetup}
          onDismiss={() => setShowPublicWebSetup(false)}
          onComplete={handlePublicWebSetupComplete}
        />

        {/* SharePoint On-Premises Connector Setup */}
        <SharePointOnPremConnectorSetup
          isVisible={showSharePointOnPremSetup}
          onDismiss={() => setShowSharePointOnPremSetup(false)}
          onComplete={handleSharePointOnPremSetupComplete}
        />

        {/* File Server Connector Setup */}
        <FileServerConnectorSetup
          isVisible={showFileServerSetup}
          onDismiss={() => setShowFileServerSetup(false)}
          onComplete={handleFileServerSetupComplete}
        />

        {/* Create Index Dialog */}
        <Dialog
          hidden={!showCreateIndexDialog}
          onDismiss={() => setShowCreateIndexDialog(false)}
          dialogContentProps={{
            type: DialogType.normal,
            title: 'Create New Search Index',
            subText: 'Create a new Azure AI Search index for your connector data.'
          }}
          modalProps={{
            isBlocking: true,
            styles: { main: { maxWidth: 450 } }
          }}
        >
          <CreateIndexDialog
            onDismiss={() => setShowCreateIndexDialog(false)}
            onIndexCreated={(indexName) => {
              setMessage({
                text: `Index "${indexName}" created successfully!`,
                type: MessageBarType.success
              });
              setShowCreateIndexDialog(false);
              loadData(); // Reload to show the new index
            }}
          />
        </Dialog>
      </Stack>
    </div>
  );
};

export default AIConnectorHub;
